import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ReadinessBadge } from "../components/Cards";
import { CourseEditor } from "../components/CourseEditor";

const STAGES = ["AMBASSADEUR", "NEW_START", "COMMERCIAL", "LEADER", "TEAM_LEADER", "CORPORATE_TRAINER", "ASSISTANT_MANAGER", "MANAGER"];
const ROLES = ["SUPER_ADMIN", "ADMIN", "ACADEMY_ADMIN", "FORMATEUR", "CORPORATE_TRAINER", "TRAINER", "LEARNER", "OWNER"];
const TABS = ["users", "registrations", "coursesTab", "assignments", "campaigns", "certifications", "knowledge", "audit"];

export default function Admin() {
  const { t, i18n } = useTranslation();
  const { user: me } = useAuth();
  const isSuper = me && ["SUPER_ADMIN", "OWNER"].includes((me as any).system_role);
  const fr = i18n.language === "fr";
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [links, setLinks] = useState<any[]>([]);
  const [certs, setCerts] = useState<any[]>([]);
  const [audit, setAudit] = useState<any[]>([]);
  const [knowledge, setKnowledge] = useState<any[]>([]);
  const [stageTarget, setStageTarget] = useState<any>(null);
  const [newStage, setNewStage] = useState("AMBASSADEUR");
  const [reason, setReason] = useState("");
  const [assignForm, setAssignForm] = useState({ learner_id: "", course_id: "", source: "MANUAL" });
  const [readiness, setReadiness] = useState<any>(null);
  const [editorTarget, setEditorTarget] = useState<{ courseId: string; version: number } | null>(null);
  const [resetRequests, setResetRequests] = useState<any[]>([]);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [nu, setNu] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", confirm: "", system_role: "LEARNER", career_stage: "AMBASSADEUR", generate_temp_password: false, force_password_change: false });

  const createNewUser = async () => {
    if (!nu.generate_temp_password && nu.password !== nu.confirm) { toast.error(fr ? "Les mots de passe ne correspondent pas" : "Passwords do not match"); return; }
    try {
      const { data } = await api.post("/admin/users", { ...nu, password: nu.generate_temp_password ? undefined : nu.password });
      toast.success(data.temp_password ? `${fr ? "Mot de passe temporaire" : "Temp password"}: ${data.temp_password}` : "OK", { duration: 15000 });
      setNu({ ...nu, first_name: "", last_name: "", email: "", phone: "", password: "", confirm: "" });
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const userAction = async (id: string, path: string, body: any, showTemp = false) => {
    try {
      const { data } = await api.post(`/admin/users/${id}/${path}`, body);
      toast.success(showTemp && data.temp_password ? `${fr ? "Mot de passe temporaire" : "Temp password"}: ${data.temp_password}` : "OK", { duration: showTemp ? 15000 : 3000 });
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };
  const [readinessLearner, setReadinessLearner] = useState<Record<string, string>>({});

  const loadAll = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/admin/assignments").then((r) => setAssignments(r.data)).catch(() => {});
    api.get("/admin/campaign-links").then((r) => setLinks(r.data)).catch(() => {});
    api.get("/admin/certifications").then((r) => setCerts(r.data)).catch(() => {});
    api.get("/admin/audit").then((r) => setAudit(r.data)).catch(() => {});
    api.get("/knowledge/categories").then((r) => setKnowledge(r.data)).catch(() => {});
    api.get("/admin/password-reset-requests").then((r) => setResetRequests(r.data)).catch(() => {});
    api.get("/admin/registrations").then((r) => setRegistrations(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const changeStage = async () => {
    try {
      await api.post(`/admin/users/${stageTarget.id}/stage`, { new_stage: newStage, reason });
      toast.success(t("admin.stageChanged"));
      setStageTarget(null); setReason("");
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const createAssignment = async () => {
    try {
      await api.post("/admin/assignments", assignForm);
      toast.success(t("admin.assigned"));
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const newVersion = async (courseId: string) => {
    try { await api.post(`/admin/courses/${courseId}/new-version`); toast.success(fr ? "Nouvelle version créée" : "New version created"); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const certAction = async (certId: string, action: string) => {
    try { await api.post(`/admin/certifications/${certId}/${action}`, { reason: "admin action" }); toast.success("OK"); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const evalReadiness = async (campaignKey: string) => {
    const lid = readinessLearner[campaignKey];
    if (!lid) return;
    try {
      const { data } = await api.get(`/admin/readiness/${campaignKey}/${lid}`);
      setReadiness(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const learners = users.filter((u) => u.system_role === "LEARNER");

  return (
    <div className="space-y-6" data-testid="admin-console">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight fade-up">{t("admin.title")}</h1>
      <div className="flex flex-wrap gap-1 border-b border-border pb-2">
        {TABS.map((tb) => (
          <button key={tb} onClick={() => setTab(tb)} data-testid={`admin-tab-${tb}`}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${tab === tb ? "bg-[#002BF6] text-white" : "hover:bg-muted"}`}>
            {t(`admin.${tb}`)}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <div className="space-y-4">
        {resetRequests.length > 0 && (
          <div className="swiss-card p-5 border-l-4 border-l-[#C8A44D]" data-testid="reset-requests-panel">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {fr ? "Demandes de réinitialisation de mot de passe" : "Password reset requests"} ({resetRequests.length})
            </p>
            <div className="mt-3 space-y-2">
              {resetRequests.map((r: any) => (
                <div key={r.id} className="flex items-center justify-between flex-wrap gap-2 text-sm border border-border rounded-md px-3 py-2" data-testid={`reset-request-${r.email}`}>
                  <span>{r.email} <span className="text-xs text-muted-foreground font-mono-v">{r.created_at?.slice(0, 16).replace("T", " ")}</span></span>
                  <span className="flex gap-2">
                    {isSuper && (
                      <button data-testid={`rr-reset-${r.email}`} onClick={() => userAction(r.user_id, "reset-password", {}, true)}
                        className="text-xs font-medium bg-[#0A1F44] text-white px-3 py-1.5 rounded-md hover:bg-[#132c5c] transition-colors duration-200">
                        {fr ? "Générer un mot de passe temporaire" : "Generate temporary password"}
                      </button>
                    )}
                    <button data-testid={`rr-resolve-${r.email}`} onClick={async () => { try { await api.post(`/admin/password-reset-requests/${r.id}/resolve`); toast.success(fr ? "Demande traitée" : "Request resolved"); loadAll(); } catch (e) { toast.error(apiError(e)); } }}
                      className="text-xs font-medium border border-border px-3 py-1.5 rounded-md hover:bg-muted transition-colors duration-200">
                      {fr ? "Marquer traité" : "Mark resolved"}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {isSuper && (
          <div className="swiss-card p-5 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="create-user-form">
            <input data-testid="nu-firstname" placeholder={fr ? "Prénom" : "First name"} value={nu.first_name} onChange={(e) => setNu({ ...nu, first_name: e.target.value })} className="px-3 py-2 border border-border rounded-md text-sm" />
            <input data-testid="nu-lastname" placeholder={fr ? "Nom" : "Last name"} value={nu.last_name} onChange={(e) => setNu({ ...nu, last_name: e.target.value })} className="px-3 py-2 border border-border rounded-md text-sm" />
            <input data-testid="nu-email" placeholder="E-mail" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} className="px-3 py-2 border border-border rounded-md text-sm" />
            <input data-testid="nu-phone" placeholder={fr ? "Téléphone (optionnel)" : "Phone (optional)"} value={nu.phone} onChange={(e) => setNu({ ...nu, phone: e.target.value })} className="px-3 py-2 border border-border rounded-md text-sm" />
            <select data-testid="nu-role" value={nu.system_role} onChange={(e) => setNu({ ...nu, system_role: e.target.value })} className="px-3 py-2 border border-border rounded-md text-sm bg-white">
              {ROLES.map((r) => <option key={r}>{r}</option>)}
            </select>
            <select data-testid="nu-stage" value={nu.career_stage} onChange={(e) => setNu({ ...nu, career_stage: e.target.value })} className="px-3 py-2 border border-border rounded-md text-sm bg-white">
              {STAGES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <input data-testid="nu-password" type="password" placeholder={fr ? "Mot de passe" : "Password"} disabled={nu.generate_temp_password} value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} className="px-3 py-2 border border-border rounded-md text-sm disabled:opacity-40" />
            <input data-testid="nu-confirm" type="password" placeholder={fr ? "Confirmation" : "Confirm"} disabled={nu.generate_temp_password} value={nu.confirm} onChange={(e) => setNu({ ...nu, confirm: e.target.value })} className="px-3 py-2 border border-border rounded-md text-sm disabled:opacity-40" />
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" data-testid="nu-gentemp" checked={nu.generate_temp_password} onChange={(e) => setNu({ ...nu, generate_temp_password: e.target.checked })} />
              {fr ? "Mot de passe temporaire" : "Temp password"}
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" data-testid="nu-force" checked={nu.force_password_change} onChange={(e) => setNu({ ...nu, force_password_change: e.target.checked })} />
              {fr ? "Forcer changement" : "Force change"}
            </label>
            <button data-testid="nu-submit" onClick={createNewUser} disabled={!nu.email}
              className="col-span-2 py-2 bg-[#0A1F44] text-white text-sm font-medium rounded-md hover:bg-[#132B5C] transition-colors duration-200 disabled:opacity-50">
              {fr ? "Créer l'utilisateur" : "Create user"}
            </button>
          </div>
        )}
        <div className="swiss-card overflow-x-auto" data-testid="admin-users-table">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-4">{t("admin.name")}</th><th className="p-4">{t("admin.email")}</th>
                <th className="p-4">{t("admin.role")}</th><th className="p-4">{t("admin.stage")}</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-border last:border-0">
                  <td className="p-4 font-medium">{u.name} {u.data_class === "DEMO" && <span className="text-[9px] font-mono-v text-zinc-400 ml-1">DEMO</span>}</td>
                  <td className="p-4 text-muted-foreground">{u.email}</td>
                  <td className="p-4"><span className="font-mono-v text-xs">{u.system_role}</span></td>
                  <td className="p-4"><span className="font-mono-v text-xs">{u.career_stage}</span></td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1.5 items-center">
                    <button onClick={() => { setStageTarget(u); setNewStage(u.career_stage); }} data-testid={`change-stage-${u.email}`}
                      className="text-xs font-medium text-[#002BF6] border border-[#002BF6] px-2 py-1 rounded-md hover:bg-[#002BF6]/5 transition-colors duration-200">
                      {t("admin.changeStage")}
                    </button>
                    {isSuper && (<>
                      <select data-testid={`role-select-${u.email}`} value={u.system_role} onChange={(e) => userAction(u.id, "role", { new_role: e.target.value })}
                        className="text-xs border border-border rounded-md px-1.5 py-1 bg-white">
                        {ROLES.map((r) => <option key={r}>{r}</option>)}
                      </select>
                      <select data-testid={`status-select-${u.email}`} value={u.status || "ACTIVE"} onChange={(e) => userAction(u.id, "status", { status: e.target.value })}
                        className="text-xs border border-border rounded-md px-1.5 py-1 bg-white">
                        {["ACTIVE", "SUSPENDED", "DISABLED"].map((s) => <option key={s}>{s}</option>)}
                      </select>
                      <button data-testid={`reset-pwd-${u.email}`} onClick={() => userAction(u.id, "reset-password", {}, true)}
                        className="text-xs border border-amber-400 text-amber-700 px-2 py-1 rounded-md hover:bg-amber-50 transition-colors duration-200">
                        {fr ? "Reset MDP" : "Reset PW"}
                      </button>
                    </>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {tab === "registrations" && (
        <div className="swiss-card p-6" data-testid="registrations-panel">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("admin.registrations")}</p>
          <div className="mt-4 space-y-2">
            {registrations.length === 0 && <p className="text-sm text-muted-foreground" data-testid="registrations-empty">{fr ? "Aucune demande d'inscription." : "No registration requests."}</p>}
            {registrations.map((r: any) => (
              <div key={r.id} className="border border-border rounded-md p-4" data-testid={`registration-${r.email}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-medium text-sm">{r.name}</p>
                    <p className="text-xs text-muted-foreground">{r.email}{r.phone ? ` · ${r.phone}` : ""} · {r.locale?.toUpperCase()}</p>
                    <p className="text-xs text-muted-foreground font-mono-v mt-0.5">{r.created_at?.slice(0, 16).replace("T", " ")}</p>
                    {r.review_note && <p className="text-xs text-muted-foreground mt-1 italic">{fr ? "Note" : "Note"}: {r.review_note}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-mono-v font-semibold px-2 py-0.5 rounded ${r.status === "PENDING_APPROVAL" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>{r.status}</span>
                    {r.status === "PENDING_APPROVAL" && (
                      <>
                        <button data-testid={`reg-approve-${r.email}`}
                          onClick={async () => { const note = window.prompt(fr ? "Note interne (optionnelle) :" : "Internal note (optional):") || ""; try { await api.post(`/admin/registrations/${r.id}/approve`, { note }); toast.success(fr ? "Inscription approuvée" : "Registration approved"); loadAll(); } catch (e) { toast.error(apiError(e)); } }}
                          className="text-xs font-medium bg-emerald-700 text-white px-3 py-1.5 rounded-md hover:bg-emerald-800 transition-colors duration-200">
                          {fr ? "Approuver" : "Approve"}
                        </button>
                        <button data-testid={`reg-reject-${r.email}`}
                          onClick={async () => { const note = window.prompt(fr ? "Raison du refus (optionnelle) :" : "Rejection reason (optional):") || ""; try { await api.post(`/admin/registrations/${r.id}/reject`, { note }); toast.success(fr ? "Inscription refusée" : "Registration rejected"); loadAll(); } catch (e) { toast.error(apiError(e)); } }}
                          className="text-xs font-medium bg-[#FF3B30] text-white px-3 py-1.5 rounded-md hover:bg-[#e0342a] transition-colors duration-200">
                          {fr ? "Refuser" : "Reject"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "coursesTab" && (
        <div className="space-y-3" data-testid="admin-courses-list">
          {courses.map((c) => (
            <div key={c.id} className="swiss-card p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div>
                <p className="font-medium">{fr ? c.title_fr : c.title_en}</p>
                <p className="text-xs text-muted-foreground font-mono-v mt-1">
                  {c.status} · {c.enrollment_policy} · {t("admin.versions")}: {c.versions.map((v: any) => `v${v.version}(${v.status[0]})`).join(" ")}
                  {c.campaign_key ? ` · ${c.campaign_key}` : ""}
                </p>
              </div>
              <div className="flex gap-2 shrink-0 flex-wrap">
                {c.versions.filter((v: any) => v.status !== "PUBLISHED" && v.status !== "ARCHIVED").map((v: any) => (
                  <button key={v.version} onClick={() => setEditorTarget({ courseId: c.id, version: v.version })}
                    data-testid={`edit-course-${c.slug}-v${v.version}`}
                    className="text-xs font-medium bg-[#0A1F44] text-white px-3 py-1.5 rounded-md hover:bg-[#132c5c] transition-colors duration-200">
                    {fr ? `Éditer v${v.version}` : `Edit v${v.version}`}
                  </button>
                ))}
                {c.versions.filter((v: any) => v.status === "PUBLISHED").map((v: any) => (
                  <button key={`view-${v.version}`} onClick={() => setEditorTarget({ courseId: c.id, version: v.version })}
                    data-testid={`view-course-${c.slug}-v${v.version}`}
                    className="text-xs font-medium border border-border px-3 py-1.5 rounded-md hover:bg-muted transition-colors duration-200">
                    {fr ? `Voir v${v.version}` : `View v${v.version}`}
                  </button>
                ))}
                <button onClick={() => newVersion(c.id)} data-testid={`new-version-${c.slug}`}
                  className="text-xs font-medium border border-border px-3 py-1.5 rounded-md hover:bg-muted transition-colors duration-200">
                  {t("admin.newVersion")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "assignments" && (
        <div className="space-y-4" data-testid="admin-assignments">
          <div className="swiss-card p-6 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium mb-1">{t("admin.learner")}</label>
              <select data-testid="assign-learner-select" value={assignForm.learner_id}
                onChange={(e) => setAssignForm({ ...assignForm, learner_id: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-white">
                <option value="">—</option>
                {learners.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">{t("admin.course")}</label>
              <select data-testid="assign-course-select" value={assignForm.course_id}
                onChange={(e) => setAssignForm({ ...assignForm, course_id: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-white">
                <option value="">—</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{fr ? c.title_fr : c.title_en}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">{t("admin.source")}</label>
              <select data-testid="assign-source-select" value={assignForm.source}
                onChange={(e) => setAssignForm({ ...assignForm, source: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-white">
                {["MANUAL", "ROLE_RECOMMENDED", "CAREER_STAGE", "CAMPAIGN"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <button onClick={createAssignment} disabled={!assignForm.learner_id || !assignForm.course_id}
              data-testid="assign-submit-button"
              className="py-2 bg-[#002BF6] text-white text-sm font-medium rounded-md hover:bg-[#0024cc] transition-colors duration-200 disabled:opacity-50">
              {t("admin.assign")}
            </button>
          </div>
          <div className="swiss-card p-5 space-y-2">
            {assignments.map((a) => {
              const u = users.find((x) => x.id === a.learner_id);
              const c = courses.find((x) => x.id === a.course_id);
              return (
                <div key={a.id} className="flex flex-wrap justify-between gap-2 text-sm border-b border-border pb-2 last:border-0">
                  <span>{u?.name || a.learner_id} → {c ? (fr ? c.title_fr : c.title_en) : a.course_id}</span>
                  <span className="font-mono-v text-xs text-muted-foreground">{a.source}{a.campaign_key ? ` · ${a.campaign_key}` : ""}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "campaigns" && (
        <div className="space-y-4" data-testid="admin-campaigns">
          {links.map((l) => (
            <div key={l.id} className="swiss-card p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-heading font-semibold">{fr ? l.title_fr : l.title_en}</h3>
                  <p className="text-xs font-mono-v text-muted-foreground mt-1">{l.campaign_key} · {l.requirements.length} req.</p>
                </div>
                <div className="flex gap-2 items-center">
                  <select data-testid={`readiness-learner-select-${l.campaign_key}`} value={readinessLearner[l.campaign_key] || ""}
                    onChange={(e) => setReadinessLearner({ ...readinessLearner, [l.campaign_key]: e.target.value })}
                    className="px-3 py-2 border border-border rounded-md text-sm bg-white">
                    <option value="">{t("admin.learner")}</option>
                    {learners.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                  <button onClick={() => evalReadiness(l.campaign_key)} data-testid={`evaluate-readiness-${l.campaign_key}`}
                    className="text-xs font-medium bg-[#002BF6] text-white px-3 py-2 rounded-md hover:bg-[#0024cc] transition-colors duration-200">
                    {t("admin.evaluate")}
                  </button>
                </div>
              </div>
              <ul className="mt-3 space-y-1">
                {l.requirements.map((r: any, i: number) => (
                  <li key={i} className="text-sm text-muted-foreground">• {fr ? r.label_fr : r.label_en} <span className="font-mono-v text-xs">[{r.type}]</span></li>
                ))}
              </ul>
              {readiness && readiness.campaign_key === l.campaign_key && (
                <div className="mt-4 border-t border-border pt-4" data-testid={`readiness-result-${l.campaign_key}`}>
                  <div className="flex items-center gap-3">
                    <ReadinessBadge status={readiness.readiness_status} />
                    <span className="text-xs text-muted-foreground font-mono-v">{readiness.last_evaluated_at?.slice(0, 19)}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 text-sm">
                    <div>
                      <p className="text-xs font-semibold text-emerald-700 uppercase">{t("readiness.met")}</p>
                      {readiness.requirements_met.map((r: any, i: number) => <p key={i} className="text-xs mt-1">✓ {fr ? r.label_fr : r.label_en}</p>)}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#FF3B30] uppercase">{t("readiness.missing")}</p>
                      {readiness.requirements_missing.map((r: any, i: number) => <p key={i} className="text-xs mt-1">✗ {fr ? r.label_fr : r.label_en} <span className="font-mono-v">[{r.reason}]</span></p>)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "certifications" && (
        <div className="swiss-card overflow-x-auto" data-testid="admin-certs-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-4">{t("admin.learner")}</th><th className="p-4">Titre</th>
                <th className="p-4">Version</th><th className="p-4">Statut</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {certs.map((c) => {
                const u = users.find((x) => x.id === c.learner_id);
                return (
                  <tr key={c.id} className="border-b border-border last:border-0">
                    <td className="p-4">{u?.name || c.learner_id.slice(0, 8)}</td>
                    <td className="p-4">{fr ? c.title_fr : c.title_en}</td>
                    <td className="p-4 font-mono-v text-xs">v{c.version}</td>
                    <td className="p-4">
                      <span className="font-mono-v text-xs">{c.effective_status}{c.recertification_required ? " · RECERT" : ""}</span>
                    </td>
                    <td className="p-4 flex gap-2">
                      <button onClick={() => certAction(c.id, "require-recert")} data-testid={`recert-btn-${c.id}`}
                        className="text-xs border border-amber-400 text-amber-700 px-2 py-1 rounded-md hover:bg-amber-50 transition-colors duration-200">
                        {t("admin.requireRecert")}
                      </button>
                      <button onClick={() => certAction(c.id, "revoke")} data-testid={`revoke-btn-${c.id}`}
                        className="text-xs border border-red-300 text-red-600 px-2 py-1 rounded-md hover:bg-red-50 transition-colors duration-200">
                        {t("admin.revoke")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "knowledge" && (
        <div className="space-y-4" data-testid="admin-knowledge">
          {knowledge.map((c) => (
            <div key={c.id} className="swiss-card p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-heading font-semibold">{fr ? c.name_fr : c.name_en}</h3>
                <span className="text-xs font-mono-v px-2 py-1 rounded-md bg-amber-50 text-amber-700" data-testid={`coverage-${c.slug}`}>
                  {c.coverage.approved}/{c.coverage.total} {fr ? "approuvés" : "approved"} · {c.coverage.content_required} CONTENT_REQUIRED
                </span>
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {c.entries.map((e: any) => (
                  <div key={e.id} className="flex items-center justify-between gap-2 border border-border rounded-md px-3 py-2 text-sm">
                    <span>{fr ? e.title_fr : e.title_en}</span>
                    <span className="flex items-center gap-2">
                      <span className={`text-[10px] font-mono-v px-1.5 py-0.5 rounded ${e.status === "APPROVED" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{e.status}</span>
                      <button data-testid={`kn-toggle-${e.id}`}
                        onClick={async () => { try { await api.put(`/knowledge/entries/${e.id}`, { enabled: !e.enabled }); toast.success(e.enabled ? (fr ? "Source désactivée pour Ask Vytali" : "Source disabled for Ask Vytali") : (fr ? "Source activée pour Ask Vytali" : "Source enabled for Ask Vytali")); api.get("/knowledge/categories").then((r) => setKnowledge(r.data)); } catch (err) { toast.error(fr ? "Seul un contenu APPROVED peut être activé pour Ask Vytali" : apiError(err)); } }}
                        className={`text-[10px] font-mono-v px-1.5 py-0.5 rounded transition-colors duration-200 ${e.enabled ? "bg-[#0A1F44] text-white" : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"}`}>
                        {e.enabled ? "Ask Vytali ✓" : "off"}
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">{fr ? "Aucune doctrine inventée : seules les entrées APPROVED (contenu officiel fourni par l'Owner) peuvent être activées pour Ask Vytali." : "No invented doctrine: only APPROVED entries (official Owner-provided content) can be enabled for Ask Vytali."}</p>
        </div>
      )}

      {tab === "audit" && (
        <div className="swiss-card p-5 space-y-2 max-h-[70vh] overflow-y-auto" data-testid="admin-audit-log">
          {audit.map((e) => {
            const actor = users.find((u: any) => u.id === e.actor_id);
            const subject = users.find((u: any) => u.id === e.subject_id);
            return (
              <div key={e.id} className="flex flex-wrap gap-x-3 gap-y-1 justify-between text-xs border-b border-border pb-2 last:border-0">
                <span className="font-mono-v font-semibold">{e.event_type}</span>
                <span className="text-muted-foreground truncate max-w-[40%]">
                  {t("admin.actor")}: {actor?.email || e.actor_id?.slice(0, 8) || "—"}
                  {e.subject_id ? ` → ${subject?.email || e.subject_id.slice(0, 8)}` : ""}
                </span>
                <span className="text-muted-foreground font-mono-v">{e.timestamp?.slice(0, 19).replace("T", " ")}</span>
              </div>
            );
          })}
        </div>
      )}

      {editorTarget && (
        <CourseEditor courseId={editorTarget.courseId} versionNo={editorTarget.version} isSuper={isSuper}
          onClose={(refresh: boolean) => { setEditorTarget(null); if (refresh) loadAll(); }} />
      )}

      {stageTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="stage-change-modal">
          <div className="bg-white rounded-md p-6 w-full max-w-md">
            <h3 className="font-heading font-semibold text-lg">{t("admin.changeStage")} — {stageTarget.name}</h3>
            <label className="block text-sm font-medium mt-4 mb-1.5">{t("admin.newStage")}</label>
            <select data-testid="stage-select" value={newStage} onChange={(e) => setNewStage(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md text-sm bg-white">
              {STAGES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <label className="block text-sm font-medium mt-4 mb-1.5">{t("admin.reason")}</label>
            <input data-testid="stage-reason-input" value={reason} onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setStageTarget(null)} data-testid="stage-cancel"
                className="px-4 py-2 text-sm border border-border rounded-md hover:bg-muted transition-colors duration-200">{t("common.cancel")}</button>
              <button onClick={changeStage} data-testid="stage-confirm"
                className="px-4 py-2 text-sm bg-[#002BF6] text-white rounded-md hover:bg-[#0024cc] transition-colors duration-200">
                {t("admin.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
